import React from "react";
import {
    Box,
    Button,
    IconButton,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { Add as AddIcon, MoreVert as MoreVertIcon } from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { SECTION_TITLE_SX } from "./impactConstants";
import { filterCountsFor, matchesFilters } from "./segments";

const BRAND = "#00438E";
// Past this many choices a row folds the rest behind "+N more", so one long
// segment (forty stakeholder groups) cannot push the register off the screen.
const VISIBLE_CHOICES = 8;

// One choice as a toggle chip: its label and its faceted count. Picking the
// chip that is already on turns the filter off again.
function ChoiceChip({ label, count, selected, onClick }) {
    const intl = useIntl();
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            aria-pressed={selected}
            aria-label={intl.formatMessage(
                { id: "impact.segment_choice_filter", defaultMessage: "{label}, {count, plural, one {# impact} other {# impacts}}" },
                { label, count }
            )}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                maxWidth: 260,
                height: 30,
                px: 1.25,
                borderRadius: 999,
                border: "1px solid",
                borderColor: selected ? BRAND : "divider",
                backgroundColor: selected ? BRAND : "background.paper",
                color: selected ? "#fff" : "text.primary",
                font: "inherit",
                fontSize: "0.8125rem",
                fontWeight: selected ? 600 : 500,
                cursor: "pointer",
                opacity: count === 0 && !selected ? 0.55 : 1,
                transition: "background-color 120ms, border-color 120ms",
                "&:hover": { borderColor: BRAND, backgroundColor: selected ? BRAND : "rgba(0, 67, 142, 0.06)" },
                "&:focus-visible": { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
        >
            <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {label}
            </Box>
            <Box
                component="span"
                aria-hidden="true"
                sx={{
                    px: 0.75,
                    borderRadius: 999,
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    lineHeight: "18px",
                    backgroundColor: selected ? "rgba(255, 255, 255, 0.22)" : "action.hover",
                    color: selected ? "#fff" : "text.secondary",
                }}
            >
                {count}
            </Box>
        </Box>
    );
}

// One segment as a row: its title (and how many choices it has) on the left,
// every choice laid out as a chip on the right, so the whole slicing is
// readable at a glance instead of hiding behind a closed "All" select. The ⋮
// edits or deletes the segment itself.
function SegmentRow({ segment, value, total, counts, onChange, onEdit, onDelete, readOnly }) {
    const intl = useIntl();
    const [menuAnchor, setMenuAnchor] = React.useState(null);
    const [expanded, setExpanded] = React.useState(false);
    const selected = value ? String(value) : "";

    const overflow = segment.choices.length - VISIBLE_CHOICES;
    const folded = overflow > 0 && !expanded;
    // A folded row still shows the choice that is filtering, wherever it sits.
    const shown = folded
        ? segment.choices.filter((c, i) => i < VISIBLE_CHOICES || String(c.id) === selected)
        : segment.choices;
    const hidden = segment.choices.length - shown.length;
    const pick = (next) => onChange(next === selected ? "" : next);

    return (
        <Box
            role="group"
            aria-label={segment.title}
            sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1, sm: 2 },
                py: 1.5,
                "&:not(:first-of-type)": { borderTop: "1px solid", borderColor: "divider" },
            }}
        >
            <Stack
                direction="row"
                alignItems="flex-start"
                spacing={0.5}
                sx={{ width: { sm: 200 }, flexShrink: 0, minWidth: 0, pt: { sm: 0.5 } }}
            >
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography
                        component="h3"
                        sx={{ fontWeight: 700, fontSize: "0.9375rem", lineHeight: 1.3, overflowWrap: "anywhere" }}
                    >
                        {segment.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        <FormattedMessage
                            id="impact.segment_choice_count"
                            defaultMessage="{count, plural, one {# choice} other {# choices}}"
                            values={{ count: segment.choices.length }}
                        />
                    </Typography>
                </Box>
                {!readOnly && (
                    <>
                        <IconButton
                            size="small"
                            onClick={(e) => setMenuAnchor(e.currentTarget)}
                            aria-label={intl.formatMessage(
                                { id: "impact.segment_menu", defaultMessage: "Segment actions for {segment}" },
                                { segment: segment.title }
                            )}
                            sx={{ color: "text.secondary", mt: -0.25 }}
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                            <MenuItem
                                onClick={() => {
                                    setMenuAnchor(null);
                                    onEdit(segment);
                                }}
                            >
                                <ListItemText>
                                    <FormattedMessage id="impact.edit_segment" defaultMessage="Edit segment" />
                                </ListItemText>
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    setMenuAnchor(null);
                                    onDelete(segment);
                                }}
                                sx={{ color: "error.main" }}
                            >
                                <ListItemText>
                                    <FormattedMessage id="impact.delete_segment" defaultMessage="Delete segment" />
                                </ListItemText>
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </Stack>

            <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                {/* No filter on this segment. Impacts left at Any also show
                    under every choice, so there is no separate "not set" chip. */}
                <ChoiceChip
                    label={intl.formatMessage({ id: "impact.segment_any", defaultMessage: "Any" })}
                    count={total}
                    selected={!selected}
                    onClick={() => onChange("")}
                />
                {shown.map((choice) => (
                    <ChoiceChip
                        key={choice.id}
                        label={choice.label}
                        count={counts[String(choice.id)] || 0}
                        selected={selected === String(choice.id)}
                        onClick={() => pick(String(choice.id))}
                    />
                ))}
                {hidden > 0 && (
                    <Button size="small" onClick={() => setExpanded(true)} sx={{ textTransform: "none", color: BRAND, minWidth: 0 }}>
                        <FormattedMessage id="impact.segment_show_more" defaultMessage="+{count} more" values={{ count: hidden }} />
                    </Button>
                )}
                {expanded && overflow > 0 && (
                    <Button size="small" onClick={() => setExpanded(false)} sx={{ textTransform: "none", color: BRAND, minWidth: 0 }}>
                        <FormattedMessage id="impact.segment_show_less" defaultMessage="Show less" />
                    </Button>
                )}
            </Stack>
        </Box>
    );
}

// The SEGMENT panel above the register: one row per segment the project has
// defined, each showing all of its choices, plus "Add segment". It replaced the area tabs and the
// group registry beneath them — two fixed levels, where a project now defines
// as many slices as it needs and names each one.
//
// Filters AND together, so the counts on the chips are FACETED: they count the
// impacts that pass every OTHER segment's filter. That is the number you would
// get by picking the choice, which is the only number worth showing beside it.
export default function SegmentFilters({
    segments = [],
    impacts = [],
    // { segment id: choice id }
    filters = {},
    onChangeFilter,
    onClearFilters,
    onAddSegment,
    onEditSegment,
    onDeleteSegment,
    readOnly = false,
}) {
    const hasFilters = Object.keys(filters).length > 0;

    return (
        <Box sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                <Typography sx={{ ...SECTION_TITLE_SX, mb: 0, flexGrow: 1 }}>
                    <FormattedMessage id="impact.segments" defaultMessage="Segments" />
                </Typography>
                {hasFilters && (
                    <Button size="small" onClick={onClearFilters} sx={{ textTransform: "none", color: "text.secondary" }}>
                        <FormattedMessage id="impact.clear_filters" defaultMessage="Clear filters" />
                    </Button>
                )}
                {!readOnly && (
                    <Button
                        size="small"
                        startIcon={<AddIcon fontSize="small" />}
                        onClick={onAddSegment}
                        sx={{ color: BRAND, textTransform: "none", flexShrink: 0 }}
                    >
                        <FormattedMessage id="impact.add_segment" defaultMessage="Add segment" />
                    </Button>
                )}
            </Stack>
            <Paper variant="outlined" sx={{ borderRadius: 2, px: 2, py: segments.length ? 0 : 2 }}>
                {segments.length === 0 && (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        <FormattedMessage
                            id="impact.no_segments"
                            defaultMessage="No segments yet. Add one — Region, Function, Seniority — to slice the register by it."
                        />
                    </Typography>
                )}
                {segments.map((segment) => {
                    const others = Object.fromEntries(
                        Object.entries(filters).filter(([id]) => id !== String(segment.id))
                    );
                    const inScope = impacts.filter((impact) => matchesFilters(impact, others));
                    return (
                        <SegmentRow
                            key={segment.id}
                            segment={segment}
                            value={filters[String(segment.id)]}
                            total={inScope.length}
                            counts={filterCountsFor(inScope, segment)}
                            onChange={(value) => onChangeFilter(segment.id, value)}
                            onEdit={onEditSegment}
                            onDelete={onDeleteSegment}
                            readOnly={readOnly}
                        />
                    );
                })}
            </Paper>
        </Box>
    );
}
